<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, reactive, ref, watchEffect } from 'vue'
import { Badge, InspectorLayout, type InspectorProvider, type ToolbarButton } from '@hantera/design-system'
import { useContext, useLocalization } from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import { useJobDefinitionDetailsStore } from '@/stores/jobs/job-definition-details-store'
import JobDefinitionSummary from './JobDefinitionSummary.vue'
import JobsTab from './JobsTab.vue'
import ScheduleTab from './ScheduleTab.vue'
import StatisticsTab from './StatisticsTab.vue'

const context = useContext()
const { jobs: jobsService } = useBackend()
const t = useLocalization('jobs')

// Get job definition ID from query parameters
const viewUri = context.currentView.value?.uri
const jobDefinitionId = ref<string>('')

watchEffect(() => {
  const uri = context.currentView.value?.uri
  if (uri) {
    const url = new URL(uri, 'http://localhost')
    jobDefinitionId.value = url.searchParams.get('id') ?? ''
  }
})

// Set view title
watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('detail.viewTitle', { definitionId: jobDefinitionId.value })
  }
})

// Create store
const store = useJobDefinitionDetailsStore(jobDefinitionId.value, { jobsService })

// Provide store to child components
provide('jobDefinitionStore', store)

// Reference to JobsTab for toolbar actions
const jobsTabRef = ref<InstanceType<typeof JobsTab> | null>(null)

// Toolbar for Jobs tab
const jobsToolbar = computed<ToolbarButton[]>(() => [
  {
    type: 'button',
    id: 'schedule',
    label: t('detail.toolbar.scheduleJob'),
    icon: 'plus',
    buttonStyle: 'primary',
    onClick: () => {
      jobsTabRef.value?.openScheduleDialog()
    },
  },
  {
    type: 'button',
    id: 'batch-schedule',
    label: t('detail.toolbar.batchSchedule'),
    icon: 'inbound',
    onClick: () => {
      jobsTabRef.value?.openBatchScheduleDialog()
    },
  },
  {
    type: 'button',
    id: 'cancel',
    label: t('detail.toolbar.cancelJob'),
    icon: 'close',
    buttonStyle: 'danger',
    disabled: !jobsTabRef.value?.canCancel,
    onClick: () => {
      jobsTabRef.value?.cancelSelectedJob()
    },
  },
])

const scheduleStatus = computed<'scheduled' | 'suspended' | null>(() => {
  const def = store.definition
  if (!def) return null
  if (def.scheduleSuspended) return 'suspended'
  if (def.schedules && def.schedules.length > 0) return 'scheduled'
  return null
})

// InspectorProvider must be reactive, not computed
const inspectorProvider = reactive<InspectorProvider>({
  title: jobDefinitionId.value,
  summary: JobDefinitionSummary,
  summaryLabel: t('detail.tabs.summary'),
  selectedTab: 'jobs',
  tabs: [
    {
      id: 'jobs',
      label: t('detail.tabs.jobs'),
      component: JobsTab,
      props: {
        jobDefinitionId: jobDefinitionId.value,
        ref: (el: InstanceType<typeof JobsTab>) => {
          jobsTabRef.value = el
        },
      },
      toolbar: jobsToolbar.value,
    },
    {
      id: 'schedule',
      label: t('detail.tabs.schedule'),
      component: ScheduleTab,
      props: {
        jobDefinitionId: jobDefinitionId.value,
      },
    },
    {
      id: 'statistics',
      label: t('detail.tabs.statistics'),
      component: StatisticsTab,
      props: {
        jobDefinitionId: jobDefinitionId.value,
      },
    },
  ],
})

// Update provider when data changes
watchEffect(() => {
  inspectorProvider.title = jobDefinitionId.value
  inspectorProvider.tabs[0].props = {
    jobDefinitionId: jobDefinitionId.value,
    ref: (el: InstanceType<typeof JobsTab>) => {
      jobsTabRef.value = el
    },
  }
  inspectorProvider.tabs[0].toolbar = jobsToolbar.value
  inspectorProvider.tabs[2].props = {
    jobDefinitionId: jobDefinitionId.value,
  }
})

let refreshInterval: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  if (jobDefinitionId.value) {
    store.refresh()

    // Auto-refresh statistics every 60 seconds
    refreshInterval = setInterval(() => {
      store.refresh()
    }, 60000)
  }
})

onBeforeUnmount(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<template>
  <InspectorLayout :provider="inspectorProvider">
    <template #header>
      <Badge v-if="scheduleStatus" :variant="scheduleStatus === 'scheduled' ? 'success' : 'warning'">
        {{ scheduleStatus === 'scheduled' ? t('detail.header.scheduled') : t('detail.header.suspended') }}
      </Badge>
    </template>
  </InspectorLayout>
</template>
